import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, CardHeader, Checkbox, Divider, Input } from '@nextui-org/react';
import { Eye, EyeOff, UserPlus, Mail, Lock, User, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const slugifyUsername = (firstname: string, lastname: string): string => {
  const base = `${firstname}.${lastname}`.toLowerCase().trim();
  return base
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9.]/g, '')
    .replace(/\.+/g, '.')
    .replace(/^\.|\.$/g, '');
};

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register, isAuthenticated } = useAuth();

  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedTermsOfUse, setAcceptedTermsOfUse] = useState(false);
  const [acceptedConditions, setAcceptedConditions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const effectiveUsername = (): string => {
    if (usernameTouched && username.trim().length > 0) {
      return username.trim();
    }
    return slugifyUsername(firstname, lastname);
  };

  const validate = (): string | null => {
    if (!firstname.trim()) return 'Bitte gib deinen Vornamen ein.';
    if (!lastname.trim()) return 'Bitte gib deinen Nachnamen ein.';
    if (!email.trim()) return 'Bitte gib deine E-Mail-Adresse ein.';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) return 'Bitte gib eine gültige E-Mail-Adresse ein.';
    if (!password) return 'Bitte vergib ein Passwort.';
    if (password.length < 6) return 'Das Passwort muss mindestens 6 Zeichen lang sein.';
    if (password !== passwordConfirm) return 'Die Passwörter stimmen nicht überein.';
    const finalUsername = effectiveUsername();
    if (!finalUsername) return 'Bitte vergib einen Benutzernamen.';
    if (!acceptedPrivacy) return 'Bitte akzeptiere die Datenschutzerklärung.';
    if (!acceptedTermsOfUse) return 'Bitte akzeptiere die Nutzungsbedingungen.';
    if (!acceptedConditions) return 'Bitte akzeptiere die AGB.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    try {
      await register({
        email: email.trim().toLowerCase(),
        firstname: firstname.trim(),
        lastname: lastname.trim(),
        password,
        username: effectiveUsername(),
      });
      navigate('/', { replace: true });
    } catch (err: any) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        (status === 409
          ? 'Diese E-Mail-Adresse oder dieser Benutzername ist bereits vergeben.'
          : status === 400
            ? 'Bitte überprüfe deine Eingaben.'
            : 'Registrierung fehlgeschlagen. Bitte versuche es später erneut.');
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = {
    inputWrapper:
      'bg-zinc-950 border-zinc-700 data-[hover=true]:border-zinc-600 group-data-[focus=true]:border-[#3fced1]',
    input: 'text-white placeholder:text-gray-500',
    label: 'text-gray-300',
  };

  return (
    <>
      <Helmet>
        <title>Konto erstellen | DartCloud</title>
        <meta name="description" content="Erstelle dein kostenloses DartCloud-Konto: Turniere finden, an Wettbewerben teilnehmen, Statistiken verfolgen und mehr." />
      </Helmet>

      <div className="min-h-[calc(100vh-6rem)] flex items-center justify-center px-4 py-12 bg-black">
        <Card className="w-full max-w-xl bg-zinc-900/80 border border-zinc-800 backdrop-blur">
          <CardHeader className="flex flex-col items-start gap-2 px-6 pt-6 pb-2">
            <div className="flex items-center gap-2">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#3fced1]/10 text-[#3fced1]">
                <UserPlus size={20} />
              </span>
              <h1 className="text-2xl font-bold text-white">Konto erstellen</h1>
            </div>
            <p className="text-sm text-gray-400">
              Erstelle dein kostenloses DartCloud-Konto und werde Teil der Community.
            </p>
          </CardHeader>

          <CardBody className="px-6 pb-6 pt-2">
            <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Vorname"
                  placeholder="Max"
                  value={firstname}
                  onValueChange={setFirstname}
                  isRequired
                  autoComplete="given-name"
                  startContent={<User size={18} className="text-gray-400" />}
                  variant="bordered"
                  classNames={inputClasses}
                />
                <Input
                  label="Nachname"
                  placeholder="Mustermann"
                  value={lastname}
                  onValueChange={setLastname}
                  isRequired
                  autoComplete="family-name"
                  startContent={<User size={18} className="text-gray-400" />}
                  variant="bordered"
                  classNames={inputClasses}
                />
              </div>

              <Input
                type="email"
                label="E-Mail"
                placeholder="deine@email.de"
                value={email}
                onValueChange={setEmail}
                isRequired
                autoComplete="email"
                startContent={<Mail size={18} className="text-gray-400" />}
                variant="bordered"
                classNames={inputClasses}
              />

              <Input
                label="Benutzername"
                placeholder={slugifyUsername(firstname, lastname) || 'max.mustermann'}
                value={username}
                onValueChange={(v) => {
                  setUsername(v);
                  setUsernameTouched(true);
                }}
                description="Wird automatisch aus deinem Namen generiert, kann aber angepasst werden."
                autoComplete="username"
                startContent={<User size={18} className="text-gray-400" />}
                variant="bordered"
                classNames={inputClasses}
              />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  label="Passwort"
                  placeholder="Mindestens 6 Zeichen"
                  value={password}
                  onValueChange={setPassword}
                  isRequired
                  autoComplete="new-password"
                  startContent={<Lock size={18} className="text-gray-400" />}
                  endContent={
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="text-gray-400 hover:text-white transition-colors"
                      aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  }
                  variant="bordered"
                  classNames={inputClasses}
                />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  label="Passwort bestätigen"
                  placeholder="Passwort wiederholen"
                  value={passwordConfirm}
                  onValueChange={setPasswordConfirm}
                  isRequired
                  autoComplete="new-password"
                  startContent={<Lock size={18} className="text-gray-400" />}
                  variant="bordered"
                  classNames={inputClasses}
                />
              </div>

              <div className="flex flex-col gap-2 mt-2">
                <Checkbox
                  isSelected={acceptedPrivacy}
                  onValueChange={setAcceptedPrivacy}
                  classNames={{ label: 'text-sm text-gray-300' }}
                >
                  Ich habe die{' '}
                  <a
                    href="/datenschutz"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#3fced1] hover:text-[#35b5b8]"
                  >
                    Datenschutzerklärung
                  </a>{' '}
                  gelesen und akzeptiert.
                </Checkbox>
                <Checkbox
                  isSelected={acceptedTermsOfUse}
                  onValueChange={setAcceptedTermsOfUse}
                  classNames={{ label: 'text-sm text-gray-300' }}
                >
                  Ich akzeptiere die{' '}
                  <a
                    href="/nutzungsbedingungen"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#3fced1] hover:text-[#35b5b8]"
                  >
                    Nutzungsbedingungen
                  </a>
                  .
                </Checkbox>
                <Checkbox
                  isSelected={acceptedConditions}
                  onValueChange={setAcceptedConditions}
                  classNames={{ label: 'text-sm text-gray-300' }}
                >
                  Ich akzeptiere die{' '}
                  <a
                    href="/agb"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#3fced1] hover:text-[#35b5b8]"
                  >
                    Allgemeinen Geschäftsbedingungen
                  </a>
                  .
                </Checkbox>
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                isLoading={isSubmitting}
                className="bg-[#3fced1] hover:bg-[#35b5b8] text-black font-semibold"
                size="lg"
              >
                {isSubmitting ? 'Konto wird erstellt …' : 'Konto erstellen'}
              </Button>
            </form>

            <Divider className="my-6 bg-zinc-800" />

            <p className="text-center text-sm text-gray-400">
              Du hast bereits ein Konto?{' '}
              <RouterLink
                to="/login"
                className="text-[#3fced1] hover:text-[#35b5b8] font-medium transition-colors"
              >
                Jetzt einloggen
              </RouterLink>
            </p>
          </CardBody>
        </Card>
      </div>
    </>
  );
};

export default RegisterPage;
